import { Ng2StateDeclaration, StatesModule, Transition } from '@uirouter/angular';
import { FiStateNames } from '../../shared/fi-state-proxy/fi-state-names';
import { FiAbstractStateComponent } from '../../shared/fi-state-proxy/fi-abstract-state.component';
import { FiTodoListComponent } from './fi-todo-list/fi-todo-list.component';
import { FiTodoEditComponent } from '@fi-app/fi-onboarding/fi-todo/fi-todo-edit/fi-todo-edit.component';
import { FiTodoHelperService } from '@fi-app/fi-onboarding/fi-todo/fi-todo-helper.service';
import { FiTodoService } from '@fi-app/fi-onboarding/fi-todo/fi-todo.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { coerceNumberProperty } from '@angular/cdk/coercion';
import { FiTodoListStoreService } from './fi-todo-edit/fi-todo-list-editor-store.service';
import { AccessRightService } from '@fi-app/shared/fi-shared-services/access-right.service';
import { ModuleType, ObjectTypes } from '@fi-shared/types';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';
import { FiTodoSectionsEditComponent } from './fi-todo-edit/fi-todo-sections-edit/fi-todo-sections-edit.component';
import { FiTodoItemEditComponent } from './fi-todo-edit/fi-todo-item-edit/fi-todo-item-edit.component';
import {
    IAccessRightFilterCollection,
} from '@fi-app/shared/models/access-right-filter-collection.model';
import { FiTodoViewComponent } from './fi-todo-view/fi-todo-view.component';

export const onTodoEditorStateExit = (transition: Transition) => {
    const injector = transition.injector();
    const fiTodoListStoreService: FiTodoListStoreService = injector.get(FiTodoListStoreService);
    fiTodoListStoreService.resetTodoListEditorStoreData();
};

const todoListEdit_selectedFiltersCollection = [
    Transition,
    FiTodoHelperService,
    (tr: Transition, fiTodoHelperService: FiTodoHelperService) => {
        return Promise.all([
            tr.injector().getAsync('todoListItem'),
            tr.injector().getAsync('savedQuickSelectionFilters'),
            tr.injector().getAsync('accessRightFilterCollection'),
        ]).then(() => {
            fiTodoHelperService.setInitialSavedQuickSelectionFilters();
        });
    },
];

const accessRightFilterCollectionResolveFn = [
    AccessRightService,
    FiTodoListStoreService,
    (accessRightService: AccessRightService, fiTodoListStoreService: FiTodoListStoreService) => {
        return new Promise<void>((resolve) => {
            accessRightService
                .getAccessRightsForPublicationByObjectType(ObjectTypes.TodoList)
                .then((accessRights: IAccessRightFilterCollection) => {
                    fiTodoListStoreService.setTodoListAccessRightFilterCollection(accessRights);
                    resolve();
                });
        });
    },
];
const savedQuickSelectionFiltersResolveFn = [
    AccessRightService,
    FiTodoListStoreService,
    (accessRightService: AccessRightService, fiTodoListStoreService: FiTodoListStoreService) => {
        return new Promise<void>((resolve) => {
            accessRightService.GetSelectionFiltersForPublication(ModuleType.Onboarding, true).then((filters) => {
                fiTodoListStoreService.setTodoListQuickSelectionFilters(filters);
                resolve();
            });
        });
    },
];

export const TodoState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Todo._name,
    url: '/Todo',
    abstract: true,
    component: FiAbstractStateComponent,
};

export const TodoListState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Todo.List,
    url: '/?:filterType&:sortColumn&:sortDirection&:offset',
    component: FiTodoListComponent,
    reloadOnSearch: false,
};

const newTodoListItemResolveFn = [
    FiTodoHelperService,
    FiTodoListStoreService,
    FiTodoService,
    FiStateService,
    Transition,
    (
        fiTodoHelperService: FiTodoHelperService,
        fiTodoListStoreService: FiTodoListStoreService,
        fiTodoService: FiTodoService,
        fiStateService: FiStateService,
        tr: Transition,
    ) => {
        return new Promise((resolve, reject) => {
            const todoListItem = fiTodoListStoreService.currentTodoListData();

            if (isNullOrUndefined(todoListItem)) {
                let newTodoListItem = fiTodoHelperService.getNewTodoListItemInstance();
                newTodoListItem = fiTodoHelperService.initContentLanguages(newTodoListItem);

                const todoListId: number = coerceNumberProperty(fiStateService.getParams(tr).todoListId);
                if (!!todoListId) {
                    fiTodoService.getTodoListCopy(todoListId).then((todoList) => {
                        todoList = fiTodoHelperService.initContentLanguages(todoList);
                        /* we set these properties at backend
                        todoList.AccessRightFilterCollection = AccessRightFilterCollectionModel.getNewInstance();
                        todoList.IsShared = true;
                        todoList.Id = undefined;*/
                        todoList.Names = fiTodoHelperService.insertCopyLabelIntoContentLanguageLabelModels(
                            todoList.Names,
                        );
                        fiTodoListStoreService.setInitialTodoListData(newTodoListItem);
                        fiTodoListStoreService.setCurrentTodoListData(todoList);
                        fiTodoHelperService.setInitialSelectedLanguageId(todoList);
                        resolve(todoList);
                    }, reject);
                } else {
                    fiTodoHelperService.setInitialSelectedLanguageId(newTodoListItem);
                    newTodoListItem.Sections = fiTodoHelperService.getSectionsWithFilledPlaceholderNames(
                        newTodoListItem.Sections,
                    );
                    fiTodoListStoreService.setInitialTodoListData(newTodoListItem);
                    fiTodoListStoreService.setCurrentTodoListData(newTodoListItem);
                    resolve(newTodoListItem);
                }
            } else {
                resolve(todoListItem);
            }
        });
    },
];

const newTodoItemResolveFn = [
    FiTodoHelperService,
    FiTodoListStoreService,
    Transition,
    (fiTodoHelperService: FiTodoHelperService, fiTodoListStoreService: FiTodoListStoreService, tr: Transition) => {
        const todoItem = fiTodoListStoreService.currentTodoListItem();
        if (isNullOrUndefined(todoItem)) {
            tr.abort();
            fiTodoHelperService.navigateToSectionsEditor();
            return null;
        }
        return todoItem;
    },
];

const todoListItemByIdResolveFn = [
    FiTodoService,
    FiStateService,
    Transition,
    FiTodoListStoreService,
    FiTodoHelperService,
    (
        fiTodoService: FiTodoService,
        fiStateService: FiStateService,
        tr: Transition,
        fiTodoListStoreService: FiTodoListStoreService,
        fiTodoHelperService: FiTodoHelperService,
    ) => {
        const todoListItem = fiTodoListStoreService.currentTodoListData();

        if (isNullOrUndefined(todoListItem)) {
            const todoListId: number = coerceNumberProperty(fiStateService.getParams(tr).id);
            return new Promise((resolve, reject) => {
                fiTodoService.getTodoList(todoListId).then((todoList) => {
                    todoList = fiTodoHelperService.initContentLanguages(todoList);
                    fiTodoListStoreService.setInitialTodoListData(todoList);
                    fiTodoListStoreService.setCurrentTodoListData(todoList);
                    fiTodoHelperService.setInitialSelectedLanguageId(todoList);
                    resolve(todoList);
                }, reject);
            });
        }

        return todoListItem;
    },
];

const todoListViewerResolveFn = [
    FiTodoService,
    FiStateService,
    Transition,
    (fiTodoService: FiTodoService, fiStateService: FiStateService, tr: Transition) => {
        const todoListId: number = coerceNumberProperty(fiStateService.getParams(tr).id);
        return fiTodoService.getTodoListViewer(todoListId);
    },
]

export const TodoAddState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Todo.Add._name,
    url: '/Add/{todoListId:[0-9]+}',
    views: {
        '@Module': {
            component: FiTodoEditComponent,
        },
    },
    resolve: {
        todoListItem: newTodoListItemResolveFn,
        accessRightFilterCollection: accessRightFilterCollectionResolveFn,
        savedQuickSelectionFilters: savedQuickSelectionFiltersResolveFn,
        todoListId: [
            FiStateService,
            Transition,
            (fiStateService: FiStateService, tr: Transition) =>
                coerceNumberProperty(fiStateService.getParams(tr).todoListId),
        ],
    },
    params: {
        todoListId: null,
    },
    onExit: onTodoEditorStateExit,
};

export const TodoAddSectionsState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Todo.Add.Sections._name,
    url: '/Sections',
    views: {
        '@Module': {
            component: FiTodoSectionsEditComponent,
        },
    },
    resolve: {
        todoListItem: newTodoListItemResolveFn,
    },
};

export const TodoAddItemState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Todo.Add.Sections.Item,
    url: '/Item',
    views: {
        '@Module': {
            component: FiTodoItemEditComponent,
        },
    },
    resolve: {
        todoListItem: newTodoListItemResolveFn,
        todoItem: newTodoItemResolveFn,
    },
};

export const TodoEditState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Todo.Edit._name,
    url: '/Edit/{id:[0-9]+}',
    views: {
        '@Module': {
            component: FiTodoEditComponent,
        },
    },
    resolve: {
        todoListItem: todoListItemByIdResolveFn,
        accessRightFilterCollection: accessRightFilterCollectionResolveFn,
        savedQuickSelectionFilters: savedQuickSelectionFiltersResolveFn,
        selectedFiltersCollection: todoListEdit_selectedFiltersCollection,
    },
    onExit: onTodoEditorStateExit,
};

export const TodoViewState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Todo.View,
    url: '/View/{id:[0-9]+}',
    views: {
        '@Module': {
            component: FiTodoViewComponent,
        },
    },
    resolve: {
        todoListViewer: todoListViewerResolveFn,
    },
};

export const TodoEditSectionsState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Todo.Edit.Sections._name,
    url: '/Sections',
    views: {
        '@Module': {
            component: FiTodoSectionsEditComponent,
        },
    },
    resolve: {
        todoListItem: todoListItemByIdResolveFn,
    },
};

export const TodoEditItemState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Todo.Edit.Sections.Item,
    url: '/Item',
    views: {
        '@Module': {
            component: FiTodoItemEditComponent,
        },
    },
    resolve: {
        todoListItem: todoListItemByIdResolveFn,
        todoItem: newTodoItemResolveFn,
    },
};

export const TodoStates: StatesModule = {
    states: [
        TodoState,
        TodoListState,
        TodoAddState,
        TodoAddSectionsState,
        TodoAddItemState,
        TodoEditState,
        TodoViewState,
        TodoEditSectionsState,
        TodoEditItemState,
    ],
};
